import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

function PortalDialog({
    show,
    mode = 'create',
    data = { portalName: '', description: '' },
    onChange,
    onClose,
    onSave,
    portals = [],
    maxPortals = 1,
    showMaxReachedError = false,
    editingPortalIdx = null
}) {
    const { t } = useTranslation('create_project/edit_page_configurations');
    const [error, setError] = useState('');
    const [portalName, setPortalName] = useState(data.portalName || '');
    const [description, setDescription] = useState(data.description || '');

    useEffect(() => {
        setPortalName(data.portalName || '');
        setDescription(data.description || '');
        setError('');
    }, [data, show]);

    const handleNameChange = (e) => {
        const value = e.target.value.slice(0, 128);
        setPortalName(value);
        onChange({ target: { name: 'portalName', value } });
        
        // Check for duplicate name and update error
        if (value.trim() && (mode === 'create' 
            ? portals.some(p => p.portalName.trim().toLowerCase() === value.trim().toLowerCase())
            : portals.some((p, idx) => idx !== editingPortalIdx && p.portalName.trim().toLowerCase() === value.trim().toLowerCase())
        )) {
            setError(t('portal_name_duplicate'));
        } else {
            setError('');
        }
    };
    
    // Check for duplicate portal name
    const hasDuplicateName = () => {
        if (!portalName.trim()) return false;
        
        if (mode === 'create') {
            return portals.some(p => p.portalName.trim().toLowerCase() === portalName.trim().toLowerCase());
        } else {
            // In edit mode, exclude the current portal being edited
            return portals.some((p, idx) => 
                idx !== editingPortalIdx && p.portalName.trim().toLowerCase() === portalName.trim().toLowerCase()
            );
        }
    };
    const handleDescriptionChange = (e) => {
        const value = e.target.value.slice(0, 512);
        setDescription(value);
        onChange({ target: { name: 'description', value } });
    };

    const selectedPortals = portals.filter(p => (
        p.entities.some(e => e.checked)
    ));
    const isMaxPortalsReached = mode === 'create' && selectedPortals.length >= maxPortals;

    const handleSave = () => {
        // Clear any previous errors
        setError('');
        
        // Check for validation errors
        if (!portalName.trim()) {
            setError(t('portal_name_required', 'Portal name is required.'));
            return;
        }
        
        if (portalName.length > 128) {
            setError(t('portal_name_too_long', 'Portal name must be 128 characters or less.'));
            return;
        }
        
        // Check for duplicate portal name (both create and edit modes)
        if (hasDuplicateName()) {
            setError(t('portal_name_duplicate'));
            return;
        }
        
        if (isMaxPortalsReached) {
            setError(t('max_portals_reached'));
            return;
        }
        
        // If no errors, proceed with save
        onSave();
    };

    return (
        <Dialog title={null} onClose={null} minWidth={550} maxWidth={550} style={{ zIndex: 2000 }}>
            <div className="p-1">
                <div className="d-flex align-items-center justify-content-between">
                    <h5 className="fw-bold mb-0">{mode === 'create' ? t('add_new_portal', 'Add New Portal') : t('edit_portal', 'Edit Portal')}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        style={{ zIndex: 2 }}
                        onClick={onClose}
                    />
                </div>
                <div className="mb-2">
                    <label className="form-label" htmlFor="portalName" required>{t('portal_name', 'Portal Name')}</label>
                    <input
                        id="portalName"
                        name="portalName"
                        className="form-control"
                        value={portalName}
                        onChange={handleNameChange}
                        placeholder={t('portal_name_placeholder', 'Enter portal name')}
                        style={{ width: '100%' }}
                        maxLength={128}
                    />
                    <div className="d-flex justify-content-start align-items-center mt-1">
                        <small className="text-muted">{portalName.length}/128</small>
                    </div>
                </div>
                <div className="mb-2">
                    <label className="form-label" htmlFor="portalDescription">{t('portal_description', 'Description')}</label>
                    <textarea
                        id="portalDescription"
                        name="description"
                        className="form-control"
                        value={description}
                        onChange={handleDescriptionChange}
                        placeholder={t('portal_description_placeholder', 'Enter description (optional)')}
                        style={{ width: '100%' }}
                        rows={3}
                        maxLength={512}
                    />
                    <div className="d-flex justify-content-start align-items-center mt-1">
                        <small className="text-muted">{description.length}/512</small>
                    </div>
                </div>
                {error && (
                    <div>
                        <small className='text-danger'>
                            {error}
                        </small>
                    </div>
                )}
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <div></div>
                <div>
                    <button className="btn btn-secondary" onClick={onClose}>{t('cancel', 'Cancel')}</button>
                    &nbsp;&nbsp;
                    <button 
                        className="btn btn-primary" 
                        onClick={handleSave}
                    >
                        {mode === 'create' ? t('add_portal', 'Add') : t('save', 'Save')}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

PortalDialog.propTypes = {
    show: PropTypes.bool.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']),
    data: PropTypes.shape({
        portalName: PropTypes.string,
        description: PropTypes.string
    }),
    onChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    portals: PropTypes.array,
    maxPortals: PropTypes.number,
    showMaxReachedError: PropTypes.bool,
    editingPortalIdx: PropTypes.number
};

export default PortalDialog;
